import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import {
  faCircleStar,
  faPause,
  faPlay,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import {
  faCircleArrowDown,
  faCircleArrowUp,
} from '@soundxyz/font-awesome/sharp-solid-svg-icons/index.mjs';
import { LikeButton } from '@components/Likes/Button';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { PolymorphicComponent } from '@components/main-components/polymorphic-component/PolymorphicComponent';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { TrendingIndicator } from '@graphql/reactQuery';
import { useHover } from '@hooks/useHover';
import { stopPropagationAndForwardClick } from '@utils/helpers';

function IconAndColorForTrendingIndicator(trendingIndicator: TrendingIndicator) {
  switch (trendingIndicator) {
    case TrendingIndicator.Up:
      return {
        icon: faCircleArrowUp,
        color: 'text-brand300',
      };
    case TrendingIndicator.Down:
      return {
        icon: faCircleArrowDown,
        color: 'text-destructive300',
      };
    case TrendingIndicator.New:
      return {
        icon: faCircleStar,
        color: 'text-warning400',
      };
    default:
      return {
        icon: faCircleStar,
        color: 'text-base400',
      };
  }
}

function IconForPlayingState(isPlaying: boolean) {
  if (isPlaying) {
    return faPause;
  } else {
    return faPlay;
  }
}

interface BaseProps {
  id: string;
  image: React.ReactNode;
  imageRadius: 'rounded-md' | 'rounded-full';
  trendingIndicator?: TrendingIndicator;
  title: {
    text: string;
    href?: string;
  };
  verified?: boolean;
  subtitle: {
    text: string;
    href?: string;
  };
  secondarySubtitle: {
    text: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  };
  actionButton?: React.ReactNode;
  likeButton?: {
    liked: boolean;
    onClick: () => void;
  };
  dropdownContent?: React.ReactNode;
  tertiarySubtitle?: {
    text: string;
    alert: boolean;
  };
  rank: number;
  isPlaying?: boolean;
}

interface DivProps {
  onClickCell: () => void;
  cellHref?: undefined;
}

interface LinkProps {
  onClickCell?: undefined;
  cellHref: string;
}

type Props = BaseProps & (DivProps | LinkProps);

export function LandingRankedListItem({
  title,
  verified = false,
  rank,
  id,
  onClickCell,
  cellHref,
  isPlaying,
  image,
  imageRadius,
  subtitle,
  secondarySubtitle,
  tertiarySubtitle,
  trendingIndicator,
  actionButton,
  likeButton,
  dropdownContent,
}: Props) {
  const [hovering, hoverProps] = useHover();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  return (
    <div className="relative cursor-pointer" {...hoverProps}>
      <PolymorphicComponent
        {...(onClickCell !== undefined
          ? {
              onClick: onClickCell,
              tabIndex: 0,
              as: 'div',
            }
          : {
              href: cellHref,
              as: 'link',
            })}
        className={`flex cursor-pointer items-center gap-3 overflow-hidden rounded-md px-2 py-2 ${
          hovering ? `bg-base200` : ``
        }`}
        data-testid={`landing-ranked-list-item-${id}`}
      >
        {/**
         * Cell Rank + Trending Indicator (conditionally rendered on hover)
         */}
        <div className="flex w-4 flex-00auto flex-col items-center">
          <motion.div
            key={`rank-${id}`}
            transition={{ duration: 0.25 }}
            layout="position"
            layoutDependency={hovering && trendingIndicator !== undefined}
            initial={false}
            className="font-numerals text-numerals-xxs font-bold"
          >
            {rank}
          </motion.div>
          {hovering && trendingIndicator !== undefined && (
            <motion.div
              key={`trending-indicator-${id}`}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.25, delay: 0.05 }}
              className=""
            >
              <FontAwesomeIcon
                icon={IconAndColorForTrendingIndicator(trendingIndicator).icon}
                className={IconAndColorForTrendingIndicator(trendingIndicator).color}
              />
            </motion.div>
          )}
        </div>
        {/**
         * Cell Image
         */}
        <div
          className={`relative aspect-square h-full w-14 flex-00auto overflow-hidden ${imageRadius}  bg-neutral200`}
        >
          <div
            className={`relative h-full w-full transition-transform ${
              hovering ? `scale-110` : ``
            } `}
          >
            {image !== undefined && (
              <>
                {typeof image === 'string' ? (
                  <Image src={image} alt={`${title}-cover`} fill />
                ) : (
                  image
                )}
              </>
            )}
          </div>

          {isPlaying !== undefined && (isPlaying || hovering) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <FontAwesomeIcon
                icon={IconForPlayingState(isPlaying)}
                className="h-10 w-10 text-white"
              />
            </div>
          )}
        </div>
        {/**
         * Cell info wrapper (titles, subtitles)
         */}
        <div className="flex flex-col gap-1.5">
          {/**
           * Top row -> playbars and title
           */}
          <div className="flex">
            {isPlaying && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                key={`play-bars-${id}`}
                className="mr-2"
                data-testid={`playing-indicator-${id}`}
              >
                <PlayIndicatorBars size="xs" />
              </motion.div>
            )}
            <motion.div
              layout="position"
              layoutDependency={isPlaying !== undefined ? isPlaying : false}
              key={`title-${id}`}
              transition={{ duration: 0.25 }}
            >
              <div className="line-clamp-1 flex items-center self-start text-ellipsis text-left font-title text-title-xxs font-medium">
                {title.href ? (
                  <Link onClick={e => e.stopPropagation()} href={title.href}>
                    {title.text}
                  </Link>
                ) : (
                  title.text
                )}
                {verified && (
                  <div className="mb-0.5 ml-1">{true && <VerifiedTooltip size={14} />}</div>
                )}
              </div>
            </motion.div>
          </div>

          {/**
           * Bottom row -> Subtitle, secondary subtitle, tertiary subtitle
           */}
          <div className="flex flex-row items-center gap-1.5 whitespace-nowrap font-base text-base-xs font-medium text-base500">
            {/**
             * Subtitle can include a link or not
             */}
            {subtitle.href ? (
              <Link
                onClick={e => e.stopPropagation()}
                href={subtitle.href}
                className="line-clamp-1"
              >
                {subtitle.text}
              </Link>
            ) : (
              <div className="line-clamp-1">{subtitle.text}</div>
            )}

            {secondarySubtitle && (
              <>
                <div className="text-[8px]">•</div>
                <div className="font-normal">
                  {secondarySubtitle.onClick ? (
                    <button
                      onClick={stopPropagationAndForwardClick(secondarySubtitle.onClick)}
                      className=""
                    >
                      {secondarySubtitle.text}
                    </button>
                  ) : (
                    secondarySubtitle.text
                  )}
                </div>
              </>
            )}
            {tertiarySubtitle && (
              <>
                <div className="text-[8px]">•</div>
                <div className={`font-normal ${tertiarySubtitle.alert ? `text-fuchsia500` : ``}`}>
                  {tertiarySubtitle.text}
                </div>
              </>
            )}
          </div>
        </div>
      </PolymorphicComponent>
      {/**
       * Context menu that only shows on hover
       * Includes 3 (optional) buttons:
       * 1. Action button
       * 2. Like button
       * 3. Three dots dropdown
       */}
      {hovering && (
        <div className="absolute bottom-0 right-0 top-0 hidden items-center justify-center gap-2 rounded-br-md rounded-tr-md bg-gradient-to-l from-base200 from-90% pl-6 pr-4 md:flex">
          {actionButton}
          {likeButton && (
            <LikeButton onClick={likeButton.onClick} liked={likeButton.liked} disabled={false} />
          )}
          {dropdownContent && (
            <ThreeDotsDropdown
              setIsDropdownOpen={setIsDropdownOpen}
              isDropdownOpen={isDropdownOpen}
              dropdownProps={{
                align: 'end',
                queue: true,
              }}
            >
              {dropdownContent}
            </ThreeDotsDropdown>
          )}
        </div>
      )}
    </div>
  );
}
